/**
 * Notification Bell Component
 *
 * Displays unacknowledged alerts as a bell icon with badge count
 * in the AppLayout header. Click opens a popover with recent alerts.
 */

import React, { useState } from 'react';
import { Badge, Popover, List, Button, Tag, Typography, Empty, Space, theme } from 'antd';
import {
  BellOutlined,
  InfoCircleOutlined,
  WarningOutlined,
  CloseCircleOutlined,
  CheckOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useUnacknowledgedAlerts, useAcknowledgeAlert } from '@/services/api/alerts';
import { formatRelativeTime } from '@/utils';
import type { AdminAlert, AlertSeverity } from '@/types';

const { Text } = Typography;

const SEVERITY_ICON: Record<AlertSeverity, React.ReactNode> = {
  info: <InfoCircleOutlined />,
  warning: <WarningOutlined />,
  critical: <CloseCircleOutlined />,
};

const SEVERITY_COLOR: Record<AlertSeverity, string> = {
  info: 'blue',
  warning: 'orange',
  critical: 'red',
};

export const NotificationBell: React.FC = () => {
  const navigate = useNavigate();
  const { token } = theme.useToken();
  const { data } = useUnacknowledgedAlerts();
  const acknowledgeMutation = useAcknowledgeAlert();
  const [acknowledgingId, setAcknowledgingId] = useState<string | null>(null);

  const alerts = data?.alerts ?? [];
  const count = alerts.length;
  const displayAlerts = alerts.slice(0, 5);

  const handleAcknowledge = async (alertId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setAcknowledgingId(alertId);
    try {
      await acknowledgeMutation.mutateAsync({ alertId });
    } catch {
      // Error handled by TanStack Query
    } finally {
      setAcknowledgingId(null);
    }
  };

  const content = (
    <div style={{ width: 360 }}>
      {displayAlerts.length === 0 ? (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description="No unacknowledged alerts"
          style={{ padding: '16px 0' }}
        />
      ) : (
        <>
          <List
            dataSource={displayAlerts}
            renderItem={(alert: AdminAlert) => (
              <List.Item
                key={alert.id}
                style={{ padding: '8px 0' }}
                actions={[
                  <Button
                    key="ack"
                    type="link"
                    size="small"
                    icon={<CheckOutlined />}
                    loading={acknowledgingId === alert.id}
                    onClick={(e) => handleAcknowledge(alert.id, e)}
                  >
                    Ack
                  </Button>,
                ]}
              >
                <List.Item.Meta
                  avatar={
                    <Tag
                      color={SEVERITY_COLOR[alert.severity]}
                      icon={SEVERITY_ICON[alert.severity]}
                      style={{ margin: 0 }}
                    >
                      {alert.severity.toUpperCase()}
                    </Tag>
                  }
                  title={
                    <Text ellipsis style={{ maxWidth: 200 }}>
                      {alert.title}
                    </Text>
                  }
                  description={
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      {formatRelativeTime(alert.createdAt)}
                    </Text>
                  }
                />
              </List.Item>
            )}
          />
          {alerts.length > 5 && (
            <div style={{ textAlign: 'center', padding: '4px 0', color: token.colorTextSecondary, fontSize: 12 }}>
              and {alerts.length - 5} more...
            </div>
          )}
        </>
      )}
      <div
        style={{
          borderTop: `1px solid ${token.colorBorderSecondary}`,
          padding: '8px 0 0',
          textAlign: 'center',
        }}
      >
        <Button type="link" onClick={() => navigate('/alerts')}>
          View All Alerts
        </Button>
      </div>
    </div>
  );

  return (
    <Popover
      content={content}
      title={
        <Space>
          <BellOutlined />
          <span>Notifications</span>
        </Space>
      }
      trigger="click"
      placement="bottomRight"
    >
      <Badge count={count} size="small" offset={[-4, 4]}>
        <BellOutlined
          style={{
            fontSize: 18,
            cursor: 'pointer',
            color: token.colorTextSecondary,
          }}
        />
      </Badge>
    </Popover>
  );
};

export default NotificationBell;
